import { useCallback, useEffect, useRef } from "react";
import InputWithSelect from "../input-with-select";
import DynamicSwitch from "./inputs/dynamic-switch";
import UISelect from "../ui-controls/select";
import UITextField from "../ui-controls/textfield";
import UseGql from "@/services/graph-ql/useGql";
import { enum_salutations } from "@/services/dropdown-enums";
import {
  getDropdownOptionsWithoutRole,
} from "@/store/slices/general/dropdownOptionsSlice";
import {
  generateOptions,
  getFormattedDate,
  isValidPhoneNumber,
} from "@/services/generic.methods";
import { useSelector } from "react-redux";
import UIPhoneWithCountryPicker from "../ui-controls/phone-with-country-picker";
import TagsInput from "../ui-controls/tags-input";
import UIAutoCompelete from "../ui-controls/auto-complete";
import RadioButtonGroup from "./inputs/radio-group";
import SocialSecurityNumber from "./inputs/ssn";
import UIDatePicker from "../ui-controls/date-field";
import UITimePicker from "../ui-controls/time-field";
import UIDateTimePicker from "../ui-controls/date-time-field";
import UIComboBox from "../ui-controls/combo-box";
import {
  emailRegx,
} from "@/services/schemas/genericSchemas";
import debounce from "lodash.debounce";
import moment from "moment";
import { selectCountries } from "@/store/slices/general/countriesSlice";

/** Incomplete numeric text that must not be passed through Number() (avoids NaN). */
const isPartialNumericInput = (raw) => {
  if (raw === "" || raw == null) return true;
  const s = String(raw).trim();
  if (s === "") return true;
  if (/^-?$/.test(s)) return true;
  if (/^-?\d*\.$/.test(s)) return true;
  if (/^-?\.\d*$/.test(s)) return true;
  return false;
};

/** Coerce dynamic number fields for Formik without ever storing NaN. */
const coerceDynamicNumberValue = (raw) => {
  if (raw === "" || raw == null) return "";
  if (typeof raw === "number") {
    return Number.isFinite(raw) ? raw : "";
  }
  const s = String(raw).trim();
  if (s === "") return "";
  if (isPartialNumericInput(s)) return s;
  const n = Number(s);
  return Number.isFinite(n) ? n : "";
};

const FormFields = (props) => {
  const {
    title,
    name,
    placeholder,
    readonly,
    disabled,
    tooltip,
    prefix,
    options,
    type,
    renderChip,
    source,
    updateOnChange = false,
    selectName,
    rules = {},
    FormikForm,
    disableSubmit,
    validateServerSide,
    saveOnChange,
    quickUpdate = false,
    serverData,
    setServerData,
    clearable = true,
    clearableLabel,
    disableFields = false,
    multiple,
    infoText,
    isImportant,
    min,
    max,
    maxLength,
    className = "",
    setInvalidFields,
    noneSelectable = false,
    serverValidation = true, // for appointment form it is not required, passing it false from parent component for appointment form
    onValidatingChange,
    invalidFields = {},
    chipDeletable = false,
  } = props;

  const { gqlValidate = null, required = false } = rules;
  const { executeQuery, loading } = UseGql(gqlValidate?.query);
  const salutations = generateOptions(enum_salutations, "name", "value");
  const dropdownOptions = useSelector(getDropdownOptionsWithoutRole);
  const countries = useSelector(selectCountries);

  useEffect(() => {
    if (onValidatingChange) {
      onValidatingChange(name, loading);
    }
  }, [loading, name]);

  /** @name: @getOptions
   *  @params: sources => Object =>
   *  { options, label of the property, value name of the property in store }
   */
  const getOptions = (source) => {
    if (source) {
      // alternateLabel is used if the label hasn't a value
      const {
        store,
        label,
        value,
        alternateLabel,
        filterKey = "countryId",
        parentStore = "countries",
        filterCallback,
      } = source;

      let newOpt = generateOptions(
        dropdownOptions[store],
        label,
        value,
        alternateLabel,
      );

      if (filterCallback) {
        const formData = FormikForm?.values?.[filterKey];

        newOpt = filterCallback(newOpt, formData, dropdownOptions[parentStore]);
      }

      return [...options, ...newOpt] || [];
    }
    return options;
  };

  const handleOnChange = async (e, code, forceStopQuickUpdate) => {
    // the following cond is for quick preview form
    // cond 1,2,3 => true from quick preview, true from validateFromServer (!true will go inside), check if value exist
    // the called function inside will validae field from serverside
    //
    if (quickUpdate && !forceStopQuickUpdate && e.target.value) {
      handleTextFieldOnChange(e, code, true);
      return;
    }
    let value = e?.target?.value ?? e?.value;
    const formData = JSON.parse(JSON.stringify(FormikForm?.values));
    let hasFieldError = !!FormikForm?.errors?.[selectName ?? name]?.length;

    if (
      (type === "inputSelectCombo" || type === "phone") &&
      e.target?.name === selectName
    ) {
      formData[selectName] = value;
      setServerData({
        ...serverData,
        [selectName]: value,
      });
    } else {
      if (e?.constructor.name === "SyntheticEvent") {
        FormikForm?.handleBlur(e);
      }
      formData[name] = value;
      if (setServerData)
        setServerData({
          ...serverData,
          [name]: noneSelectable ? value : value || "",
        });
    }
    if (type == "auto-complete") {
      value = value || 0;
      formData[name] = value;
    }

    // Only save if there are no errors in the entire form
    if (typeof saveOnChange === "function" && updateOnChange) {
      // Wait for Formik to validate and get fresh errors
      const errors = await FormikForm.validateForm();
      const hasAnyError = Object.keys(errors).length > 0;
      let isChanged =
        !(value === "" && serverData[name] === undefined) &&
        !(value === serverData[name] || value === serverData[selectName]);
      if (isChanged && !hasAnyError) {
        saveOnChange(formData, name);
      }
    } else if (type !== "inputSelectCombo" && type !== "phone") {
      const isCountryChange = e?.target?.name === "countryId";

      if (e?.target?.name === "countryId" && value === 3) {
        FormikForm.setTouched({ ...FormikForm.touched, state: true });
      }

      if (type == "number") {
        value = coerceDynamicNumberValue(value);
      }
      if (type == "time-picker") {
        value = value ? moment(value).format("HH:mm") : value;
      }

      FormikForm.setFieldValue(name, noneSelectable ? value : (value ?? ""));
      if (isCountryChange) {
        FormikForm.setFieldValue("stateProvinceId", "");
        FormikForm.setFieldValue("state", "");
      }
    }
  };

  const validateFromServer = async (e) => {
    let countryCode = FormikForm.values["countryCode"];
    const isInvalid = await validateServerSide({
      e,
      countryCode,
      rules,
      executeQuery,
      errors: FormikForm?.errors?.[e?.target?.name],
    });
    if (gqlValidate === null || !isInvalid) {
      FormikForm?.handleBlur(e);
      handleOnChange(e, null, true);
    }
    if (typeof saveOnChange === "function" && isInvalid) {
      saveOnChange(null);
    }
  };

  const handleTextFieldOnChange = (e, code, doValidate) => {
    // handles form changes
    FormikForm?.handleChange(e);

    const { name, value } = e?.target;

    const isValidEmail = emailRegx.test(value || "");
    const isValidPhone =
      name === "phone" &&
      (!value ||
        isValidPhoneNumber(
          value,
          FormikForm.values?.countryISOCode,
          countries,
        ));
    const isValidCountryCode =
      name === "countryCode" &&
      (code == FormikForm?.values?.countryISOCode ||
        !FormikForm?.values?.countryISOCode);

    if (name === "countryCode" && value) {
      FormikForm?.handleChange({
        target: { name: "countryISOCode", value: code },
      });
      if (setServerData)
        setServerData({
          ...serverData,
          countryCode: value,
          countryISOCode: code,
        });
    } else if (code !== undefined) {
      FormikForm?.handleChange({
        target: { name: "countryISOCode", value: code },
      });
      FormikForm?.handleChange({
        target: { name: "countryCode", value: value },
      });
      if (setServerData)
        setServerData({
          ...serverData,
          countryCode: value,
          countryISOCode: code,
        });
    }

    const isValidInput =
      isValidEmail ||
      isValidPhone ||
      (isValidCountryCode && FormikForm?.values?.phone);
    const validationKeyIncluded = ["countryCode", "email", "phone"].includes(
      name,
    );
    if (value && validationKeyIncluded && !isValidInput) {
      clearInvalidFieldsError();
      return;
    }
    if (!value) {
      if (!required) {
        setTimeout(() => {
          clearFieldError();
        });
      }
      return;
    }

    // Check if it is not quickUpdate or force validate from handleOnChange method
    const doForceValidate = !quickUpdate || doValidate;

    // Check if saveOnChange is not a function (passes automatically)
    const isNotFunction = typeof saveOnChange !== "function";
    if (
      gqlValidate &&
      ((validationKeyIncluded && isValidInput) || value) &&
      (doForceValidate || isNotFunction) &&
      serverValidation
    ) {
      debouncedChangeHandler(e);
    }
  };

  const handlePhoneChanged = (e) => {
    // if (!e.nativeEvent.inputType && e.target.value) {
    //   validateFromServer(e);
    // }
    FormikForm?.handleChange(e);
  };

  const handleTagsChanged = (selectedTags) => {
    console.log(selectedTags, "tagsChanged");
    FormikForm?.handleChange({ target: { name: "tags", value: selectedTags } });
  };

  // Custom function to clear a field error and remove the key if the error is empty
  const clearFieldError = () => {
    const newErrors = { ...FormikForm?.errors };
    delete newErrors[name]; // Remove the key if error is empty or undefined
    FormikForm?.setErrors(newErrors); // Update Formik's error state
    clearInvalidFieldsError(); // remove errors when field is empty
  };

  const clearInvalidFieldsError = () => {
    setInvalidFields((prev) => {
      let err = { ...prev };
      delete err[name];
      return err;
    });
  };

  const validateFromServerRef = useRef(validateFromServer);
  useEffect(() => {
    validateFromServerRef.current = validateFromServer;
  }, [validateFromServer]);

  const debouncedChangeHandler = useCallback(
    debounce((e) => {
      validateFromServerRef.current?.(e);
    }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
    };
  }, [debouncedChangeHandler]);

 // let error = FormikForm?.touched[name] && FormikForm?.errors[name];
  let error =
    (FormikForm?.touched[name] || invalidFields[name]) &&
    FormikForm?.errors[name];

  const titleWithIsImportant = title + (isImportant ? " (Important)" : "");
  switch (type) {
    case "text":
    case "number":
      return (
        <>
          <UITextField
            type={type == "phone" ? "text" : type}
            name={name}
            // multiline={type == "text-area" && 4}
            value={FormikForm?.values[name] ?? ""}
            title={titleWithIsImportant}
            // placeholder={placeholder || title}
            onChange={handleTextFieldOnChange}
            onBlur={(e) => {
              FormikForm?.handleBlur(e);
              handleOnChange(e);
            }}
            tooltip={tooltip}
            prefix={prefix}
            error={error}
            disabled={disabled || disableFields}
            size={"medium"}
            required={required}
            maxLength={maxLength}
          />
        </>
      );
    case "date":
      return (
        <UITextField
          name={name}
          value={
            getFormattedDate(
              FormikForm?.values[name],
              name === "createdOn" ? "DD, MMM YYYY" : "MM/DD/YYYY hh:mm a"
            ) || ""
          }
          size={"medium"}
          title={titleWithIsImportant}
          disabled={disabled}
        />
      );
    case "phone":
      return (
        <>
          <UIPhoneWithCountryPicker
            name={name}
            value={FormikForm?.values[name] || ""}
            title={titleWithIsImportant}
            onChange={handleTextFieldOnChange}
            onBlur={(e) => {
              FormikForm?.handleBlur(e);
              handleOnChange(e);
            }}
            required={required}
            error={error}
            disabled={disabled || disableFields}
            selectValue={FormikForm?.values["countryISOCode"] || ""}
            selectName={selectName}
            infoText={infoText || tooltip}
            size={"medium"}
            tooltip={tooltip}
          />
        </>
      );
    case "text-area":
      return (
        <UITextField
          type={type}
          name={name}
          multiline={true}
          rows={4}
          value={FormikForm?.values[name] || ""}
          title={titleWithIsImportant}
          // placeholder={placeholder}
          onChange={FormikForm?.handleChange}
          onBlur={FormikForm?.handleBlur}
          tooltip={tooltip}
          prefix={prefix}
          error={error}
          required={required}
          disabled={disabled || disableFields}
        />
      );

    case "select":
      return (
        <>
          <UISelect
            name={name}
            value={
              noneSelectable
                ? FormikForm?.values[name]
                : FormikForm?.values[name] || ""
            }
            title={titleWithIsImportant}
            onChange={handleOnChange}
            onBlur={FormikForm?.handleBlur}
            options={getOptions(source)}
            clearable={clearable}
            clearableLabel={clearableLabel}
            error={error}
            disabled={disabled || disableFields}
            multiple={multiple}
            required={required}
            size={"medium"}
            tooltip={tooltip}
            renderChip={renderChip}
            renderMenuChip={renderChip}
          />
        </>
      );

    case "auto-complete":
      return (
        <UIAutoCompelete
          // styles={{ minWidth: "450px" }}
          handleChange={handleOnChange}
          // handleTextChange={(text) => console.log("textchange", text)}
          options={getOptions(source)}
          loading={false}
          name={name}
          selected={
            getOptions(source).find(
              (v) => v.value == FormikForm?.values[name],
            ) || {}
          }
          required={required}
          error={FormikForm.touched[name] && FormikForm.errors[name]}
          handleBlur={FormikForm.handleBlur}
          liStyles={{ alignSelf: "end", width: "100%" }}
          variant="outlined"
          isFilterWrapper={false}
          isMultiSelectEnable={multiple}
          freeSolo={multiple}
          title={titleWithIsImportant}
          disabled={disabled || disableFields}
          size={"medium"}
        />
      );

    case "switch":
      return (
        <>
          <DynamicSwitch
            title={titleWithIsImportant}
            value={FormikForm?.values[name] || false}
            name={name}
            defaultStyled={false}
            onChange={(e) => {
              FormikForm?.handleChange(e);
              FormikForm.setFieldValue(name, e.target.value);
            }}
            tooltip={tooltip}
            onBlur={FormikForm?.handleBlur}
            disabled={disabled || disableFields}
            error={FormikForm.touched[name] && FormikForm.errors[name]}
            size="medium"
          />
        </>
      );
    case "inputSelectCombo": {
      const handleComboChange = (e) => {
        FormikForm?.handleChange(e);
        if (
          rules.required &&
          e.target.name === name &&
          !String(e.target.value ?? "").trim()
        ) {
          FormikForm.setFieldTouched(name, true, false);
          FormikForm.validateField(name);
        }
      };
      return (
        <>
          <InputWithSelect
            title={titleWithIsImportant}
            value={FormikForm?.values[name] || ""}
            selectValue={FormikForm?.values[selectName] || "none"}
            name={name}
            selectName={selectName}
            handleChange={handleComboChange}
            handleSaveData={handleOnChange}
            options={salutations}
            error={error}
            disabled={disabled || disableFields}
            required={required}
            size={"medium"}
          />
        </>
      );
    }

    case "tagsInput":
      return (
        <>
          <TagsInput
            title={titleWithIsImportant}
            name={name}
            value={FormikForm?.values["tags"] || null}
            handleChange={handleTagsChanged}
            disabled={disabled || disableFields}
            tagOptions={getOptions(source)}
            // colorOptions={getOptions(props.colorSource)}
            savedTags={serverData?.leadTags || FormikForm?.values["leadTags"]}
            size={"medium"}
          />
        </>
      );

    case "checkbox-group":
      return (
        <UISelect
          name={name}
          title={titleWithIsImportant}
          value={FormikForm?.values[name] || []}
          options={getOptions(source) || options || []}
          multiple={true}
          checkboxItems={true}
          clearable={false}
          onChange={handleOnChange}
          error={error}
          disabled={disabled || disableFields}
          tooltip={tooltip}
          size={"medium"}
        />
      );

    case "multiselect":
      return (
        <UISelect
          name={name}
          title={titleWithIsImportant}
          value={Array.isArray(FormikForm?.values[name]) ? FormikForm?.values[name] : []}
          options={getOptions(source) || options || []}
          multiple={true}
          renderChips={true}
          chipDeletable={chipDeletable}
          clearable={false}
          onChange={handleOnChange}
          error={error}
          disabled={disabled || disableFields}
          tooltip={tooltip}
          size={"medium"}
        />
      );
    case "radio":
      return (
        <>
          <RadioButtonGroup
            name={name}
            value={FormikForm?.values[name] || []}
            title={titleWithIsImportant}
            onChange={handleOnChange}
            options={getOptions(source)}
            error={error}
            disabled={disabled || disableFields}
            tooltip={tooltip}
          />
        </>
      );

    case "ssn":
      return (
        <>
          <SocialSecurityNumber
            name={name}
            value={FormikForm?.values[name] || ""}
            title={titleWithIsImportant}
            onChange={(event) => {
              handleOnChange(event);
              const nextValue = String(event?.value ?? "");
              if (/^\d{3}-\d{2}-\d{4}$/.test(nextValue)) {
                FormikForm?.setFieldTouched(name, true, false);
              }
            }}
            onBlur={() => {
              FormikForm?.setFieldTouched(name, true);
            }}
            options={getOptions(source)}
            error={error}
            disabled={disabled || disableFields}
            size={"medium"}
            required={required}
          />
        </>
      );

    case "date-picker":
      return (
        <>
          <UIDatePicker
            name={name}
            value={FormikForm?.values[name] || ""}
            title={titleWithIsImportant}
            placeholder={title}
            onChange={handleOnChange}
            size={"medium"}
            error={error}
            sx={{ width: "100%" }}
            format="DD-MMM-YYYY"
            tooltip={tooltip}
            minDate={min}
            maxDate={max}
            disabled={disabled || disableFields}
            required={required}
            // helperText={}
          />
        </>
      );

    case "time-picker":
      return (
        <>
          <UITimePicker
            name={name}
            value={FormikForm?.values[name] || ""}
            title={titleWithIsImportant}
            placeholder={title}
            onChange={handleOnChange}
            size={"medium"}
            error={error}
            sx={{ width: "100%" }}
            format="hh:mm A"
            tooltip={tooltip}
            disabled={disabled || disableFields}
            nonUTC={true}
            required={required}
            // helperText={}
            iconSx={{
              color: "var(--palette-text) !important",
            }}
          />
        </>
      );
    case "date-time-picker":
      return (
        <>
          <UIDateTimePicker
            name={name}
            value={FormikForm?.values[name] || ""}
            title={titleWithIsImportant}
            placeholder={title}
            onChange={handleOnChange}
            size={"medium"}
            error={error}
            sx={{ width: "100%" }}
            format="DD-MMM-YYYY hh:mm A"
            tooltip={tooltip}
            minDate={min}
            maxDate={max}
            isLocal={false}
            required={required}
            disabled={disabled || disableFields}
            // helperText={}
          />
        </>
      );
    case "combo-box":
      return (
        <>
          <UIComboBox
            name={name}
            value={FormikForm?.values[name] || []}
            title={titleWithIsImportant}
            onChange={handleOnChange}
            onBlur={FormikForm?.handleBlur}
            options={getOptions(source)}
            clearable={clearable}
            error={error}
            disabled={disabled || disableFields}
            multiple={multiple}
            size={"medium"}
            tooltip={tooltip}
            required={required}
            className={className}
            // helperText={}
          />
        </>
      );

    default:
      return type;
  }
};

export default FormFields;
